import { useState, useEffect, useRef, useCallback, PropsWithChildren } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../../../popover";
import { Button } from "../../../button";

import { FileDropzone } from "./FileDropzone";

interface AddFilePopoverProps extends PropsWithChildren {
  onAddFiles: (fileUrls: string[]) => void;
  popoverPortalContainer?: HTMLElement | null;
}

export const AddFilePopover = ({
  onAddFiles,
  children,
  popoverPortalContainer,
}: AddFilePopoverProps) => {
  const [isAddFileOpen, setIsAddFileOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [convertedFileUrls, setConvertedFileUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const convertFileToBase64 = useCallback(
    (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === "string") {
            resolve(reader.result);
          } else {
            reject(new Error("Failed to convert file to base64"));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      }),
    [],
  );

  const handleFileSelect = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      try {
        const newFiles = [...selectedFiles, ...files];
        setSelectedFiles(newFiles);

        const base64Promises = files.map(file => convertFileToBase64(file));
        const newBase64Data = await Promise.all(base64Promises);
        setConvertedFileUrls(prev => [...prev, ...newBase64Data]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to convert files to base64:", error);
      } finally {
        setIsUploading(false);
      }
    },
    [convertFileToBase64, selectedFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files);
      }
    },
    [handleFileSelect],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        handleFileSelect(Array.from(files));
      }
    },
    [handleFileSelect],
  );

  const handleRemoveFileAtIndex = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setConvertedFileUrls(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    setIsDisabled(selectedFiles.length === 0);
  }, [selectedFiles.length]);

  const resetState = () => {
    setSelectedFiles([]);
    setConvertedFileUrls([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = () => {
    setIsAddFileOpen(false);
    onAddFiles(convertedFileUrls);
    resetState();
  };

  const handleCancel = () => {
    setIsAddFileOpen(false);
    resetState();
  };

  const getButtonText = () => {
    if (isUploading) {
      return "Adding...";
    }
    const fileCount = selectedFiles.length;
    return `Add ${fileCount} File${fileCount !== 1 ? "s" : ""}`;
  };

  return (
    <Popover open={isAddFileOpen} onOpenChange={setIsAddFileOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-96 p-4" align="end" portalContainer={popoverPortalContainer}>
        <div className="space-y-4">
          <div className="text-sm font-medium">Add Files</div>

          <FileDropzone
            isDragOver={isDragOver}
            isUploading={isUploading}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onFileInputChange={handleFileInputChange}
            selectedFiles={selectedFiles}
            onRemoveFileAtIndex={handleRemoveFileAtIndex}
            multiple
          />

          <div className="flex justify-end space-x-2">
            <Button variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button disabled={isDisabled || isUploading} size="sm" onClick={handleSubmit}>
              {getButtonText()}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
